/**
 * Swiper system entry point.
 *
 * Imported by `src/js/core.js`. Owns every `.swiper-section` element on
 * the page via the plugin pipeline in `core/initializer.js`. Plugins are
 * registered below in declaration order; topological sort honors any
 * `runsAfter` dependencies a plugin declares.
 *
 * Module ownership: this directory is the **only** place that imports the
 * `swiper` package. `core/initializer.js` is the sole consumer of the
 * Swiper constructor; consumers elsewhere in the theme should drive
 * carousels via the data-attribute/plugin contract rather than constructing
 * their own Swiper instances. Importing `swiper` from outside this tree
 * isn't a bundle-bloat issue (Vite/Rollup dedupes module evaluation) but
 * it bypasses the ownership/lifecycle guarantees this system provides.
 * See `library/swiper/Swiper-Architecture.md` Common Pitfalls.
 *
 * Side effect: importing this module auto-bootstraps on DOMContentLoaded
 * (or immediately if the document is already past loading). Tests that
 * import lifecycle helpers directly are unaffected because they don't
 * load this entry file.
 */

// Swiper styles, imported per-module instead of via the full bundle.
// Audit: the only Swiper modules with markup-driven styling in this theme are
// pagination + navigation. data-effect/data-thumbs/data-scrollbar/data-zoom/
// data-parallax/data-keyboard/data-mousewheel are never set on any production
// section (verified 2026-04-28). If a future section opts into one of those,
// add the corresponding `swiper/css/<module>` import here.
//
// These live with the swiper system rather than in `src/js/core.js` so the
// system owns its own CSS surface area. Vite's `cssCodeSplit: true` plus
// this file's inclusion in the `core.js` chunk's module graph means the
// imports still emit into `assets/core.css` — same output, more cohesive
// ownership.
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import { Initializer } from './core/initializer.js';
import { installBackCompat } from './utils/back-compat.js';

import { mobileFlip } from './features/mobile-flip.js';
import { nestedCarousel } from './features/nested-carousel.js';
import { nestedCarouselGroup } from './features/nested-carousel-group.js';
import { syncedSwipers } from './features/synced-swipers.js';
import { autoplay } from './features/autoplay.js';
import { cardFlip } from './features/card-flip.js';
import { recenterHeadline } from './features/recenter-headline.js';
import { tabSystem } from './features/tab-system.js';
import { analytics } from './features/analytics.js';
// As features get converted, add them here. Order in this array is the
// default ordering when plugins have no `runsAfter` dependency on each other.
//
// `tabSystem` is the gate plugin (data-toggled-swiper sections). It must
// appear in the plugin list so `findGate` can claim qualifying sections,
// but it doesn't define parseOptions/afterInit/afterAllInit/onDestroy
// hooks, so its position relative to other plugins is irrelevant.
//
// `nestedCarouselGroup` declares `runsAfter: ['nested-carousel']` and the
// topological sort honors that regardless of where it sits in this list.

const plugins = [
  mobileFlip,
  nestedCarousel,
  nestedCarouselGroup,
  syncedSwipers,
  autoplay,
  cardFlip,
  recenterHeadline,
  tabSystem,
  analytics,
];

let initializer = null;

/**
 * Boot the swiper system. Idempotent: calling twice replaces the existing
 * initializer with a new one (handy during HMR).
 *
 * Failure mode: if plugin registration is invalid (duplicate names, unknown
 * `runsAfter`, cycles — all dev-time bugs caught by `sortPlugins`), we log
 * loudly and bail without booting. The page still renders; carousels just
 * don't init. That's a strictly better outcome than the previous behavior,
 * where a single bad `runsAfter` typo would throw out of bootstrap and
 * crash `installBackCompat` along with it, leaving every legacy global
 * undefined too.
 */
export function bootstrap() {
  if (initializer) {
    initializer.destroy();
  }
  // Install back-compat shims FIRST. They don't depend on the plugin list,
  // and stable globals are more valuable than carousels when something is
  // broken (consumers like `reinitSwipers` won't ReferenceError).
  installBackCompat({ plugins, Initializer });
  let next;
  try {
    next = new Initializer({ plugins });
  } catch (err) {
    console.error(
      '[swiper] Plugin registration failed; carousels will not init. ' +
        'This is a dev-time bug — check plugin names and `runsAfter` ' +
        'declarations against the list in `src/js/app/swiper/index.js`.',
      err
    );
    return null;
  }
  initializer = next;
  initializer.init();
  // Public surface: `window.swiperInitializer` is the live Initializer
  // instance. Two consumers depend on it:
  //   1. `window.reinitSwipers` (back-compat shim) defers to
  //      `window.swiperInitializer.refresh()` to avoid leaking a fresh
  //      resize listener per call.
  //   2. Devtools / smoke tests inspect the registry through it.
  // The leading-underscore alias is kept for one release as a deprecation
  // cushion.
  if (typeof window !== 'undefined') {
    window.swiperInitializer = initializer;
    // Deprecated alias — remove after consumers migrate.
    window.__swiperInitializer = initializer;
  }
  return initializer;
}

export { Initializer, plugins };

/**
 * Shopify Section Rendering API integration.
 *
 * `shopify:section:load` fires when a section's HTML is replaced in place,
 * which happens in three real-world scenarios:
 *   1. The Theme Editor (customizer) when a merchant edits a section's
 *      settings — Shopify swaps the section markup live.
 *   2. The Shopify CLI dev server (`yarn start`) when a section file is
 *      saved — same swap mechanism, different trigger.
 *   3. Theme code that calls Shopify's Section Rendering API to refresh a
 *      single section (e.g. variant change, cart update, ajax search).
 *
 * Without this listener, the section's old swiper instances would remain in
 * the registry pointing at detached DOM, and the new section element would
 * never get init'd. `Initializer.refresh()` handles both halves: sweeps the
 * stale registry entries and runs an init pass for any newly-eligible
 * section element.
 *
 * Idempotent and cheap. Safe to call on every load event.
 */
function handleSectionLoad() {
  if (initializer) {
    initializer.refresh();
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  } else {
    bootstrap();
  }

  // HMR guard. Vite's HMR can re-evaluate this module on save; without this
  // flag every reload would stack another `shopify:section:load` listener,
  // and `Initializer.refresh()` would fire 2x, 3x, 4x… per section swap
  // after a few hot-reloads. Production never re-evaluates the module, so
  // the flag is a no-op there. Stored on `window` (not module-local)
  // because the module-local binding is re-created on each evaluation.
  if (!window.__swiperSectionLoadInstalled) {
    document.addEventListener('shopify:section:load', handleSectionLoad);
    window.__swiperSectionLoadInstalled = true;
  }
}
